import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from './contexts/ToastContext'
import { DialogProvider } from './contexts/DialogContext'
import { SubscriptionProvider } from './contexts/SubscriptionContext'
import BetaIndustrySelectionModal from './components/BetaIndustrySelectionModal'
import { PWAUpdatePrompt } from './components/PWAUpdatePrompt'
import { PWAIOSHint } from './components/PWAIOSHint'
import AspiredTracker from './components/AspiredTracker'
import { applyTheme } from './utils/theme'
import type { ThemeValue } from './utils/theme'

// Eager: critical-path entry points (public landing, auth screens).
// Everything else is lazy-loaded so first paint ships only what's needed.
import Landing from './pages/Landing'
import Login from './pages/Login'
import Register from './pages/Register'

const Onboarding = lazy(() => import('./pages/Onboarding'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const TalonsLibrary = lazy(() => import('./pages/TalonsLibrary'))
const Brain = lazy(() => import('./pages/Brain'))
const DocumentLibrary = lazy(() => import('./pages/DocumentLibrary'))
const AgentPage = lazy(() => import('./pages/AgentPage'))
const AgentDetailPage = lazy(() => import('./pages/AgentDetailPage'))
const Settings = lazy(() => import('./pages/Settings'))
const CheckoutV2 = lazy(() => import('./pages/CheckoutV2'))
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'))
const AgentCustomize = lazy(() => import('./pages/AgentCustomize'))
const Integrations = lazy(() => import('./pages/Integrations'))
const StripeSettings = lazy(() => import('./pages/StripeSettings'))
const ContentCenter = lazy(() => import('./pages/ContentCenter'))
const SocialHub = lazy(() => import('./pages/SocialHub'))
const IndustryPage = lazy(() => import('./pages/IndustryPage'))
// Phase 2: IndustryAgentPage was deleted — /agents/:agentId on AgentPage
// now handles core + industry + support agents in one consolidated page.
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Notifications = lazy(() => import('./pages/Notifications'))
const Results = lazy(() => import('./pages/Results'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const DataDeletionStatus = lazy(() => import('./pages/DataDeletionStatus'))
const Trust = lazy(() => import('./pages/Trust'))
const Contact = lazy(() => import('./pages/Contact'))
const Pricing = lazy(() => import('./pages/Pricing'))
const AgentsList = lazy(() => import('./pages/AgentsList'))
const IndustriesList = lazy(() => import('./pages/IndustriesList'))
const FAQ = lazy(() => import('./pages/FAQ'))
const Compare = lazy(() => import('./pages/Compare'))
const AiForRealEstate = lazy(() => import('./pages/AiForRealEstate'))
const AiForRestaurants = lazy(() => import('./pages/AiForRestaurants'))
const AiForEcommerce = lazy(() => import('./pages/AiForEcommerce'))
const AiForLawFirms = lazy(() => import('./pages/AiForLawFirms'))
const AiForSmallBusiness = lazy(() => import('./pages/AiForSmallBusiness'))
const AiForAccountants = lazy(() => import('./pages/AiForAccountants'))
const AiForRetail = lazy(() => import('./pages/AiForRetail'))
const WhatsNew = lazy(() => import('./pages/WhatsNew'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const WhiteGlove = lazy(() => import('./pages/WhiteGlove'))

// Admin bundle — lazy so non-staff users never download it
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'))
const AdminBlogEditor = lazy(() => import('./pages/admin/AdminBlogEditor'))
const AdminChangelog = lazy(() => import('./pages/admin/AdminChangelog'))
const AdminInviteCodes = lazy(() => import('./pages/admin/AdminInviteCodes'))
const AdminSiteSettings = lazy(() => import('./pages/admin/AdminSiteSettings'))
const AdminReferrals = lazy(() => import('./pages/admin/AdminReferrals'))
const AdminAgents = lazy(() => import('./pages/admin/AdminAgents'))
const AdminAgentEditor = lazy(() => import('./pages/admin/AdminAgentEditor'))
const AdminIndustryAgents = lazy(() => import('./pages/admin/AdminIndustryAgents'))
const AdminIndustryAgentEditor = lazy(() => import('./pages/admin/AdminIndustryAgentEditor'))
const AdminIndustries = lazy(() => import('./pages/admin/AdminIndustries'))
const AdminIndustryEditor = lazy(() => import('./pages/admin/AdminIndustryEditor'))
const AdminTalons = lazy(() => import('./pages/admin/AdminTalons'))
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'))
const AdminPoll = lazy(() => import('./pages/admin/AdminPoll'))
const AdminSuggestions = lazy(() => import('./pages/admin/AdminSuggestions'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminConversations = lazy(() => import('./pages/admin/AdminConversations'))
const AdminSage = lazy(() => import('./pages/admin/AdminSage'))
const AdminRefunds = lazy(() => import('./pages/admin/AdminRefunds'))
const BugReports = lazy(() => import('./pages/admin/BugReports'))
const AdminWhiteGlove = lazy(() => import('./pages/admin/AdminWhiteGlove'))
const AdminSubscriptionWarningDemo = lazy(() => import('./pages/admin/AdminSubscriptionWarningDemo'))

// Account shell — Phase 4d
const AccountLayout = lazy(() => import('./pages/account/AccountLayout'))
const AccountOverview = lazy(() => import('./pages/account/AccountOverview'))
// Phase 4e — real billing page (replaces stub)
const AccountBilling = lazy(() => import('./pages/account/AccountBilling'))
// Phase 4f-2 — real agents + settings pages (replace stubs)
const AccountAgents = lazy(() => import('./pages/account/AccountAgents'))
const AccountSettings = lazy(() => import('./pages/account/AccountSettings'))
// Phase 4g — agency seats
const AccountSeats = lazy(() => import('./pages/account/AccountSeats'))
const AcceptSeat = lazy(() => import('./pages/AcceptSeat'))

// Known public agent slugs — keeps them from matching the catch-all.
// Must include every core agent returned by /api/agents/ or the guard
// below bounces its /:slug page back to home (finn + harper were missing).
const AGENT_SLUGS = ['luna', 'eva', 'max', 'noah', 'tara', 'grace', 'aiden', 'quinn', 'finn', 'harper']

// Reverse alias: legacy /leo URLs redirect to /quinn for old bookmarks/SEO.
// Maintained for 1-2 years post-rename, then remove.
const SLUG_ALIASES: Record<string, string> = {
  'leo': 'quinn',
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

// Guard: only render AgentDetailPage for valid agent slugs, otherwise redirect home.
// Legacy aliases (e.g. /leo) redirect to the canonical slug (/quinn) so the URL bar
// reflects the new name and old bookmarks resolve cleanly.
function AgentDetailPageGuard() {
  const { agentId: rawAgentId } = useParams()
  const raw = rawAgentId ?? ''
  if (raw in SLUG_ALIASES) {
    return <Navigate to={`/${SLUG_ALIASES[raw]}`} replace />
  }
  if (!AGENT_SLUGS.includes(raw)) {
    return <Navigate to="/" replace />
  }
  return <AgentDetailPage />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()
  if (loading) return <div className="loading-screen">Loading…</div>
  if (!isAuthenticated) {
    // Phase 9 — abandoned-cart email links land on /checkout/v2 with
    // ?plan=X&cycle=Y. If the click happens while logged out, stash the
    // intent into pendingCheckout (same shape PricingSection writes) so
    // Phase 7's post-login resumption hooks bring the user right back.
    if (location.pathname === '/checkout/v2') {
      const params = new URLSearchParams(location.search)
      const plan = params.get('plan')
      const cycle = params.get('cycle') || 'monthly'
      if (plan) {
        sessionStorage.setItem(
          'pendingCheckout',
          JSON.stringify({ plan_slug: plan, cycle }),
        )
      }
    }
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function StaffRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAuthenticated } = useAuth()
  if (loading) return <div className="loading-screen">Loading…</div>
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (user && !user.is_staff) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <div className="loading-screen">Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  if (user.onboarding_complete) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Forces a full remount when navigating between different agents
function AgentPageKeyed() {
  const { agentId } = useParams()
  return <AgentPage key={agentId} />
}

// Phase 2: legacy /agents/industry/:agentId URLs (and any user-bookmarked
// industry chat URL) redirect to the unified /agents/:agentId path.
function IndustryAgentRedirect() {
  const { agentId } = useParams()
  return <Navigate to={`/agents/${agentId ?? ''}`} replace />
}

function AppRoutes() {
  const { user, loading } = useAuth()

  useEffect(() => {
    if (user) {
      applyTheme(user.theme_preference)
    } else {
      const saved = localStorage.getItem('theme') as ThemeValue | null
      applyTheme(saved ?? 'system')
    }
  }, [user])

  if (loading) return <div className="loading-screen">Loading…</div>

  return (
    <Suspense fallback={<div className="loading-screen">Loading…</div>}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route
        path="/onboarding"
        element={
          <OnboardingGuard>
            <Onboarding />
          </OnboardingGuard>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <Dashboard />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/talons"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <TalonsLibrary />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/brain"
        element={
          <ProtectedRoute>
            <Brain />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentLibrary />
          </ProtectedRoute>
        }
      />
      {/* Legacy alias: /agents/leo → /agents/quinn (post-rename redirect) */}
      <Route path="/agents/leo" element={<Navigate to="/agents/quinn" replace />} />
      <Route path="/agents/leo/customize" element={<Navigate to="/agents/quinn/customize" replace />} />
      <Route
        path="/agents/:agentId"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <AgentPageKeyed />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <Settings />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <Integrations />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations/stripe"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <StripeSettings />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-center"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <ContentCenter />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <SocialHub />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:agentId/customize"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <AgentCustomize />
            )}
          </ProtectedRoute>
        }
      />
      {/* Phase 5a-2 — legacy /checkout removed; redirect to public pricing page */}
      <Route path="/checkout" element={<Navigate to="/pricing" replace />} />
      <Route
        path="/checkout/success"
        element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout/v2"
        element={
          <ProtectedRoute>
            <CheckoutV2 />
          </ProtectedRoute>
        }
      />
      {/* Phase 5a-2 — legacy /billing removed; redirect to /account/billing */}
      <Route path="/billing" element={<Navigate to="/account/billing" replace />} />
      {/* Phase 4d — Account shell with nested routes */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <AccountLayout />
            )}
          </ProtectedRoute>
        }
      >
        <Route index element={<AccountOverview />} />
        <Route path="agents" element={<AccountAgents />} />
        <Route path="billing" element={<AccountBilling />} />
        <Route path="seats" element={<AccountSeats />} />
        <Route path="settings" element={<AccountSettings />} />
      </Route>
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/results"
        element={
          <ProtectedRoute>
            {user && !user.onboarding_complete ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <Results />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/white-glove"
        element={
          <ProtectedRoute>
            <WhiteGlove />
          </ProtectedRoute>
        }
      />
      {/* Phase 4g — agency seat acceptance (public, no auth) */}
      <Route path="/accept-seat/:token" element={<AcceptSeat />} />
      {/* Legacy agency redirects — the Workspace path was removed; the
          canonical agency experience is the agency Plan (via /pricing) and
          seat management under /account/seats. */}
      <Route path="/agency" element={<Navigate to="/account/seats" replace />} />
      {/* Phase 2 redirect: legacy industry-agent path → unified /agents/:agentId.
          Kept as a redirect (not deleted) so existing bookmarks still resolve. */}
      <Route path="/agents/industry/:agentId" element={<IndustryAgentRedirect />} />
      {/* Admin panel — staff only */}
      <Route path="/admin" element={<StaffRoute><AdminDashboard /></StaffRoute>} />
      <Route path="/admin/blog" element={<StaffRoute><AdminBlog /></StaffRoute>} />
      <Route path="/admin/blog/new" element={<StaffRoute><AdminBlogEditor /></StaffRoute>} />
      <Route path="/admin/blog/:id/edit" element={<StaffRoute><AdminBlogEditor /></StaffRoute>} />
      <Route path="/admin/changelog" element={<StaffRoute><AdminChangelog /></StaffRoute>} />
      <Route path="/admin/users" element={<StaffRoute><AdminUsers /></StaffRoute>} />
      <Route path="/admin/conversations" element={<StaffRoute><AdminConversations /></StaffRoute>} />
      <Route path="/admin/notifications" element={<StaffRoute><AdminNotifications /></StaffRoute>} />
      <Route path="/admin/poll" element={<StaffRoute><AdminPoll /></StaffRoute>} />
      <Route path="/admin/suggestions" element={<StaffRoute><AdminSuggestions /></StaffRoute>} />
      <Route path="/admin/agents" element={<StaffRoute><AdminAgents /></StaffRoute>} />
      <Route path="/admin/agents/:agentId/edit" element={<StaffRoute><AdminAgentEditor /></StaffRoute>} />
      <Route path="/admin/industry-agents" element={<StaffRoute><AdminIndustryAgents /></StaffRoute>} />
      <Route path="/admin/industry-agents/:agentId/edit" element={<StaffRoute><AdminIndustryAgentEditor /></StaffRoute>} />
      <Route path="/admin/industries" element={<StaffRoute><AdminIndustries /></StaffRoute>} />
      <Route path="/admin/industries/:slug/edit" element={<StaffRoute><AdminIndustryEditor /></StaffRoute>} />
      <Route path="/admin/talons" element={<StaffRoute><AdminTalons /></StaffRoute>} />
      <Route path="/admin/invite-codes" element={<StaffRoute><AdminInviteCodes /></StaffRoute>} />
      <Route path="/admin/referrals" element={<StaffRoute><AdminReferrals /></StaffRoute>} />
      <Route path="/admin/settings" element={<StaffRoute><AdminSiteSettings /></StaffRoute>} />
      <Route path="/admin/sage" element={<StaffRoute><AdminSage /></StaffRoute>} />
      <Route path="/admin/refunds" element={<StaffRoute><AdminRefunds /></StaffRoute>} />
      <Route path="/admin/bugs" element={<StaffRoute><BugReports /></StaffRoute>} />
      <Route path="/admin/white-glove" element={<StaffRoute><AdminWhiteGlove /></StaffRoute>} />
      <Route path="/admin/warning-demo" element={<StaffRoute><AdminSubscriptionWarningDemo /></StaffRoute>} />
      {/* Public legal pages — no auth required */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/data-deletion-status" element={<DataDeletionStatus />} />
      <Route path="/trust" element={<Trust />} />
      {/* Legacy agency signup → canonical pricing page (agency plan lives there) */}
      <Route path="/agency/signup" element={<Navigate to="/pricing" replace />} />
      {/* Public industry pages — no auth required */}
      <Route path="/industries/:slug" element={<IndustryPage />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/compare" element={<Compare />} />
      {/* Google Ads landing pages — public, no auth */}
      <Route path="/ai-for-real-estate-agents" element={<AiForRealEstate />} />
      <Route path="/ai-for-restaurants" element={<AiForRestaurants />} />
      <Route path="/ai-for-ecommerce" element={<AiForEcommerce />} />
      <Route path="/ai-for-law-firms" element={<AiForLawFirms />} />
      <Route path="/ai-for-small-business" element={<AiForSmallBusiness />} />
      <Route path="/ai-for-accountants" element={<AiForAccountants />} />
      <Route path="/ai-for-retail" element={<AiForRetail />} />
      {/* Phase 3a-fix — public browse pages for core agents + industries */}
      <Route path="/agents" element={<AgentsList />} />
      <Route path="/industries" element={<IndustriesList />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/whats-new" element={<WhatsNew />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* Public agent detail pages — no auth required */}
      <Route path="/:agentId" element={<AgentDetailPageGuard />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  )
}

// iOS Safari viewport height fix — sets --app-height to actual visible height
// so sidebars don't get cut off by the browser chrome
function IOSViewportFix() {
  useEffect(() => {
    const set = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)
    }
    set()
    window.addEventListener('resize', set)
    window.addEventListener('orientationchange', set)
    return () => {
      window.removeEventListener('resize', set)
      window.removeEventListener('orientationchange', set)
    }
  }, [])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <IOSViewportFix />
      <ScrollToTop />
      <AspiredTracker />
      <AuthProvider>
        <SubscriptionProvider>
          <ToastProvider>
            <DialogProvider>
              <PWAUpdatePrompt />
              <PWAIOSHint />
              <AppRoutes />
              <BetaIndustrySelectionModal />
            </DialogProvider>
          </ToastProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
